import _get from 'lodash/get';
import _set from 'lodash/set';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, ListGroup, Modal } from 'react-bootstrap';
import DEFAULT_TEXTS from 'src/texts';
import Schema from 'src/types/schema';
import { Locale, LocalizedTexts } from 'src/types/general';
import { traverseSchema } from 'src/app/schema';

interface ContextValue {
	defaultLocale?: Locale;
	locale?: Locale;
	locales?: Locale[];
	onChangeLocale?: () => void;
	onLocalizePrepareInitialValues?: (schema: Schema, values: any) => void;
	onLocalizeBeforeSubmit?: (schema: Schema, values: any) => void;
	_: (key: string) => string;
}

export interface LocaleChangeArgs {
	defaultLocale: Locale;
	locale: Locale;
	locales: Locale[];
	onLocalizePrepareInitialValues: (schema: Schema, values: any) => any;
	onLocalizeBeforeSubmit: (schema: Schema, values: any) => any;
}

export const LocaleContext = React.createContext<ContextValue>({
	_: (s) => s,
});

interface LocaleProviderProps {
	localeKey?: string;
	locales?: Locale[];
	localizedTexts?: LocalizedTexts;
	bypassLocalizedTextsUntouchedDefault?: boolean;
	onLocaleChange?: (localeKey: string) => void;
	onLocaleChangeArgs?: (args: Partial<LocaleChangeArgs>) => void;
	children: any;
}

function LocaleProvider(props: LocaleProviderProps) {
	const {
		localeKey,
		locales,
		localizedTexts,
		bypassLocalizedTextsUntouchedDefault,
		onLocaleChange,
		onLocaleChangeArgs,
	} = props;
	const defaultLocaleKey = localeKey || (locales && locales.length > 0 ? locales[0].key : undefined);
	const [currentKey, setCurrentKey] = useState(defaultLocaleKey);
	const [showSelect, setShowSelect] = useState(false);

	const onChangeLocale = useCallback(() => {
		setShowSelect(true);
	}, []);

	const onLocalizePrepareInitialValues = useCallback(
		(schema, values) => {
			if (!defaultLocaleKey || bypassLocalizedTextsUntouchedDefault) return values;
			let newValues = { ...values };
			traverseSchema(schema as any, (n: any) => {
				if ((n.props || {}).localized) {
					const value = _get(values, n.name);
					const defaultTransVal = _get(value, defaultLocaleKey);
					(locales || []).forEach((locale) => {
						if (locale.key !== defaultLocaleKey) {
							const currentTransVal = _get(value, locale.key);
							const isNotTranslated = currentTransVal === defaultTransVal;
							if (isNotTranslated) {
								newValues = _set(newValues, `${n.name}.${locale.key}`, '');
							}
						}
					});
				}
				return n;
			});
			return newValues;
		},
		[locales]
	);

	const onLocalizeBeforeSubmit = useCallback(
		(schema, values) => {
			if (!defaultLocaleKey || bypassLocalizedTextsUntouchedDefault) return values;
			let newValues = { ...values };
			traverseSchema(schema as any, (n: any) => {
				if ((n.props || {}).localized) {
					const value = _get(values, n.name);
					const defaultTransVal = _get(value, defaultLocaleKey);
					(locales || []).forEach((locale) => {
						if (locale.key !== defaultLocaleKey) {
							const currentTransVal = _get(value, locale.key);
							const isNotTranslated = `${currentTransVal || ''}`.length === 0;
							if (isNotTranslated) {
								newValues = _set(newValues, `${n.name}.${locale.key}`, defaultTransVal);
							}
						}
					});
				}
				return n;
			});
			return newValues;
		},
		[locales]
	);

	const onSetLocale = useCallback((key: string) => {
		setCurrentKey(key);
		if (onLocaleChange) onLocaleChange(key);
		handleClose();
	}, []);

	const _ = useCallback(
		(key: string) => {
			if (localizedTexts) {
				const t1 = _get(localizedTexts, `${currentKey}.${key}`);
				if (t1) {
					return t1;
				}
				const t2 = _get(localizedTexts, `default.${key}`);
				if (t2) {
					return t2;
				}
			}
			return _get(DEFAULT_TEXTS, `${key}`);
		},
		[currentKey, localizedTexts]
	);

	const handleClose = useCallback(() => {
		setShowSelect(false);
	}, []);

	const defaultLocale = useMemo(() => {
		if (locales && defaultLocaleKey) {
			return locales.find((l) => l.key === defaultLocaleKey);
		}
		return undefined;
	}, [defaultLocaleKey, locales]);

	const locale = useMemo(() => {
		if (locales && currentKey) {
			return locales.find((l) => l.key === currentKey);
		}
		return undefined;
	}, [currentKey, locales]);

	useEffect(() => {
		if (onLocaleChangeArgs) {
			onLocaleChangeArgs({
				defaultLocale,
				locale,
				locales,
				onLocalizePrepareInitialValues,
				onLocalizeBeforeSubmit,
			});
		}
	}, [defaultLocale, locale, locales, onLocalizePrepareInitialValues, onLocalizeBeforeSubmit]);

	return (
		<LocaleContext.Provider
			value={{
				defaultLocale,
				locale,
				locales,
				onChangeLocale,
				onLocalizePrepareInitialValues,
				onLocalizeBeforeSubmit,
				_,
			}}
		>
			{props.children}
			{locales && locales.length > 0 && (
				<Fragment>
					<Modal show={showSelect} onHide={handleClose}>
						<Modal.Header closeButton>
							<Modal.Title>Seleziona lingua</Modal.Title>
						</Modal.Header>
						<Modal.Body>
							<ListGroup>
								{locales.map((l) => {
									return (
										<ListGroup.Item
											key={l.key}
											action
											active={currentKey === l.key}
											onClick={() => onSetLocale(l.key)}
										>
											{l.label}
										</ListGroup.Item>
									);
								})}
							</ListGroup>
						</Modal.Body>
						<Modal.Footer>
							<Button variant='secondary' onClick={handleClose}>
								Annulla
							</Button>
						</Modal.Footer>
					</Modal>
				</Fragment>
			)}
		</LocaleContext.Provider>
	);
}

export default LocaleProvider;
